import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { API_KEY, LIVE_API_BASE_URL, getApiHeaders } from '../config/api';

const liveApiBase = `${LIVE_API_BASE_URL.replace(/\/$/, '')}/v1/live`;

type FrameAssetKind = 'mov' | 'webm' | 'webp';

type LiveFrame = {
  frameId: string;
  name: string;
  movUrl: string;
  webmUrl: string;
  thumbnailUrl: string;
  isActive: boolean;
  createdAt?: string;
};

type PresignResponse = {
  frameId: string;
  uploadUrl: string;
  fileKey: string;
  fileUrl: string;
};

/** Mirrors LIVE_FRAME_ASSET_RULES in tezda-live. A UX affordance only — the backend re-checks. */
const ASSET_RULES: Record<
  FrameAssetKind,
  { label: string; extension: string; accept: string; contentTypes: string[]; maxBytes: number; platform: string }
> = {
  mov: {
    label: 'iOS animation (.mov)',
    extension: '.mov',
    accept: '.mov,video/quicktime',
    contentTypes: ['video/quicktime', 'video/x-quicktime', 'application/octet-stream', ''],
    maxBytes: 25 * 1024 * 1024,
    platform: 'iOS',
  },
  webm: {
    label: 'Android animation (.webm)',
    extension: '.webm',
    accept: '.webm,video/webm',
    contentTypes: ['video/webm', 'application/octet-stream', ''],
    maxBytes: 25 * 1024 * 1024,
    platform: 'Android',
  },
  webp: {
    label: 'Picker thumbnail (.webp)',
    extension: '.webp',
    accept: '.webp,image/webp',
    contentTypes: ['image/webp'],
    maxBytes: 1 * 1024 * 1024,
    platform: 'Picker',
  },
};

const formatBytes = (bytes: number): string =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;

function validateAsset(file: File, kind: FrameAssetKind): string | null {
  const rule = ASSET_RULES[kind];
  const name = file.name.toLowerCase();
  if (!name.endsWith(rule.extension)) return `Must be a ${rule.extension} file — got "${file.name}".`;
  if (file.type && !rule.contentTypes.includes(file.type)) {
    return `Unexpected content type "${file.type}" for a ${rule.extension} file.`;
  }
  if (file.size > rule.maxBytes) return `Too large — ${formatBytes(file.size)}, limit is ${formatBytes(rule.maxBytes)}.`;
  if (file.size === 0) return 'File is empty.';
  return null;
}

type AssetState = {
  file: File | null;
  error: string;
  previewUrl: string;
  presign: PresignResponse | null;
};

const emptyAsset: AssetState = { file: null, error: '', previewUrl: '', presign: null };

const KIND_TO_URL_KEY: Record<FrameAssetKind, keyof LiveFrame> = {
  mov: 'movUrl',
  webm: 'webmUrl',
  webp: 'thumbnailUrl',
};

/**
 * Edits an existing frame: replace any subset of the three assets, keep the rest as-is.
 *
 * The frame to edit is read from router state (passed by LiveFrames.tsx, which already has it from
 * the catalog list) rather than fetched again — there is no GET /v1/live/frames/:id, and the list
 * page's data is already current. A hard refresh on this URL with no state falls back to fetching
 * the whole catalog page and finding the id, so a reload or a shared link still works.
 */
const EditLiveFrame: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { frameId: routeFrameId } = useParams<{ frameId: string }>();

  const inputRefs = {
    mov: useRef<HTMLInputElement>(null),
    webm: useRef<HTMLInputElement>(null),
    webp: useRef<HTMLInputElement>(null),
  };

  const [loadingFrame, setLoadingFrame] = useState(true);
  const [frame, setFrame] = useState<LiveFrame | null>((location.state as { frame?: LiveFrame } | null)?.frame ?? null);
  const [name, setName] = useState(frame?.name ?? '');
  const [assets, setAssets] = useState<Record<FrameAssetKind, AssetState>>({
    mov: { ...emptyAsset },
    webm: { ...emptyAsset },
    webp: { ...emptyAsset },
  });
  const [saving, setSaving] = useState(false);
  const [uploadingKind, setUploadingKind] = useState<FrameAssetKind | null>(null);

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found. Please log in.');
      return null;
    }
    if (!API_KEY) {
      toast.error('Missing API key. Set REACT_APP_API_KEY in your .env file.');
      return null;
    }
    return getApiHeaders(token);
  }, []);

  // Fallback path: no router state (direct link / refresh). Load the catalog page-by-page until the
  // id turns up — there is no single-frame GET, and the catalog is rarely large enough for this to
  // cost more than one request in practice.
  useEffect(() => {
    if (frame || !routeFrameId) {
      setLoadingFrame(false);
      return;
    }
    const headers = headerWithAuth();
    if (!headers) {
      setLoadingFrame(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        for (let page = 1; page <= 20; page += 1) {
          const res = await fetch(`${liveApiBase}/frames?page=${page}&limit=50&activeOnly=false`, { headers });
          const json = await res.json();
          if (!res.ok) throw new Error(json.message || 'Failed to load frame');
          const data = json.data as { frames: LiveFrame[]; hasMore: boolean };
          const found = data.frames.find((f) => f.frameId === routeFrameId);
          if (found) {
            if (!cancelled) {
              setFrame(found);
              setName(found.name);
            }
            return;
          }
          if (!data.hasMore) break;
        }
        if (!cancelled) toast.error('Frame not found.');
      } catch (error) {
        if (!cancelled) toast.error((error as Error).message || 'Failed to load frame');
      } finally {
        if (!cancelled) setLoadingFrame(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeFrameId]);

  useEffect(
    () => () => {
      Object.values(assets).forEach((asset) => {
        if (asset.previewUrl) URL.revokeObjectURL(asset.previewUrl);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const kinds: FrameAssetKind[] = ['mov', 'webm', 'webp'];

  const handleFileChange = (kind: FrameAssetKind, file: File | null) => {
    setAssets((prev) => {
      if (prev[kind].previewUrl) URL.revokeObjectURL(prev[kind].previewUrl);
      if (!file) return { ...prev, [kind]: { ...emptyAsset } };
      const error = validateAsset(file, kind);
      return {
        ...prev,
        [kind]: {
          file,
          error: error || '',
          previewUrl: error ? '' : URL.createObjectURL(file),
          presign: null,
        },
      };
    });
  };

  const anyError = kinds.some((k) => assets[k].error !== '');
  const anyReplacement = kinds.some((k) => assets[k].file !== null);

  /**
   * Presigns and uploads ONE replacement asset immediately on selection, tied to the existing
   * frameId (backend support added alongside this page — POST /v1/live/frames/presign now accepts
   * an optional frameId). Unlike the create flow, uploads here are per-asset rather than batched:
   * an admin editing a frame is usually swapping one file, and gating that on selecting all three
   * again would be a worse edit experience than the create page's all-or-nothing flow.
   */
  const uploadReplacement = async (kind: FrameAssetKind, file: File) => {
    if (!frame) return;
    const headers = headerWithAuth();
    if (!headers) return;

    setUploadingKind(kind);
    try {
      const presignRes = await fetch(`${liveApiBase}/frames/presign`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ name: name.trim() || frame.name, kind, frameId: frame.frameId }),
      });
      const presignJson = await presignRes.json();
      if (!presignRes.ok) throw new Error(presignJson.message || `Failed to get upload URL for ${ASSET_RULES[kind].extension}`);
      const presign = presignJson.data as PresignResponse;

      const uploadRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        body: await file.arrayBuffer(),
        headers: { 'Content-Type': ASSET_RULES[kind].contentTypes[0] || 'application/octet-stream' },
      });
      if (!uploadRes.ok) throw new Error(`Failed to upload ${ASSET_RULES[kind].extension}`);

      setAssets((prev) => ({ ...prev, [kind]: { ...prev[kind], presign } }));
      toast.success(`${ASSET_RULES[kind].platform} asset uploaded — Save Changes to apply.`);
    } catch (error) {
      toast.error((error as Error).message || 'Upload failed');
      setAssets((prev) => ({ ...prev, [kind]: { ...emptyAsset } }));
    } finally {
      setUploadingKind(null);
    }
  };

  const onFileSelected = (kind: FrameAssetKind, file: File | null) => {
    handleFileChange(kind, file);
    if (file && !validateAsset(file, kind)) {
      void uploadReplacement(kind, file);
    }
  };

  /**
   * A replacement counts as "ready" once its presigned upload has completed; an asset the admin
   * didn't touch is ready by definition (the existing URL is reused). Save waits for every pending
   * upload so it never persists a stale key alongside a new one that hasn't finished landing.
   */
  const anyUploadPending = kinds.some((k) => assets[k].file && !assets[k].presign && !assets[k].error);
  const canSave = Boolean(frame) && name.trim().length > 0 && !anyError && !anyUploadPending && !saving;

  const handleSave = async () => {
    if (!frame) return;
    const headers = headerWithAuth();
    if (!headers) return;

    // Whatever was replaced and successfully uploaded wins; everything else falls back to the
    // frame's current URL. saveFrame's toUrl() accepts a full URL as-is (it only prefixes CDN_BASE
    // onto a bare key), so passing the existing URL straight through is exactly correct here.
    const movKey = assets.mov.presign?.fileKey || frame.movUrl;
    const webmKey = assets.webm.presign?.fileKey || frame.webmUrl;
    const thumbnailKey = assets.webp.presign?.fileKey || frame.thumbnailUrl;

    setSaving(true);
    try {
      const res = await fetch(`${liveApiBase}/frames`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ frameId: frame.frameId, name: name.trim(), movKey, webmKey, thumbnailKey }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to save frame');
      toast.success(json.message || 'Frame updated');
      navigate('/live-frames');
    } catch (error) {
      toast.error((error as Error).message || 'Failed to save frame');
    } finally {
      setSaving(false);
    }
  };

  if (loadingFrame) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
        <Navbar />
        <div className='max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-sm text-gray-400'>Loading frame...</div>
      </div>
    );
  }

  if (!frame) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
        <Navbar />
        <div className='max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-4'>
          <p className='text-sm text-gray-400'>Frame not found.</p>
          <button
            onClick={() => navigate('/live-frames')}
            className='px-4 py-2 text-sm font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition'
          >
            Back to Frames
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      <div className='max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6'>
        <div className='flex items-center justify-between'>
          <div>
            <h1 className='text-2xl sm:text-3xl font-bold text-white'>Edit Live Frame</h1>
            <p className='text-sm text-gray-400'>
              Replace any asset, or just rename. Assets you don't touch stay exactly as they are.
            </p>
          </div>
          <button
            onClick={() => navigate('/live-frames')}
            className='px-4 py-2 text-sm font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition'
          >
            Back to Frames
          </button>
        </div>

        <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 space-y-5'>
          <div>
            <label className='block text-xs text-gray-400 mb-1'>Frame Name *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FF9C15]/60'
            />
          </div>

          <div className='text-xs text-gray-500 font-mono'>ID: {frame.frameId}</div>

          {kinds.map((kind) => {
            const rule = ASSET_RULES[kind];
            const asset = assets[kind];
            const currentUrl = frame[KIND_TO_URL_KEY[kind]] as string;
            const isUploading = uploadingKind === kind;
            const isReplaced = Boolean(asset.presign);

            return (
              <div key={kind} className='border-t border-white/10 pt-4'>
                <div className='flex items-center justify-between mb-2'>
                  <label className='block text-xs text-gray-400'>{rule.label}</label>
                  <span className='text-[10px] uppercase tracking-wider text-gray-500'>max {formatBytes(rule.maxBytes)}</span>
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <p className='text-[10px] uppercase tracking-wider text-gray-500 mb-1'>Current</p>
                    {kind === 'webp' ? (
                      <img
                        src={currentUrl}
                        alt='Current thumbnail'
                        className='h-24 w-24 object-cover rounded-lg border border-white/10 bg-[conic-gradient(#334155_0_25%,#1e293b_0_50%,#334155_0_75%,#1e293b_0)] bg-[length:16px_16px]'
                      />
                    ) : (
                      <video
                        src={currentUrl}
                        muted
                        loop
                        autoPlay
                        playsInline
                        className='h-28 rounded-lg border border-white/10 bg-[conic-gradient(#334155_0_25%,#1e293b_0_50%,#334155_0_75%,#1e293b_0)] bg-[length:16px_16px]'
                      />
                    )}
                  </div>

                  <div>
                    <p className='text-[10px] uppercase tracking-wider text-gray-500 mb-1'>
                      {isReplaced ? 'New (will save)' : 'Replace'}
                    </p>
                    <input
                      ref={inputRefs[kind]}
                      type='file'
                      accept={rule.accept}
                      disabled={isUploading}
                      onChange={(e) => onFileSelected(kind, e.target.files?.[0] || null)}
                      className='w-full text-xs text-gray-300 disabled:opacity-50'
                    />

                    {isUploading && <div className='mt-2 text-xs text-gray-400'>Uploading...</div>}

                    {asset.error && (
                      <div className='mt-2 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-xs text-red-200'>
                        {asset.error}
                      </div>
                    )}

                    {asset.previewUrl && !isUploading && !asset.error && kind === 'webp' && (
                      <img
                        src={asset.previewUrl}
                        alt='Replacement preview'
                        className='mt-2 h-24 w-24 object-cover rounded-lg border border-emerald-400/40 bg-[conic-gradient(#334155_0_25%,#1e293b_0_50%,#334155_0_75%,#1e293b_0)] bg-[length:16px_16px]'
                      />
                    )}
                    {asset.previewUrl && !isUploading && !asset.error && kind !== 'webp' && (
                      <video
                        src={asset.previewUrl}
                        muted
                        loop
                        autoPlay
                        playsInline
                        className='mt-2 h-28 rounded-lg border border-emerald-400/40 bg-[conic-gradient(#334155_0_25%,#1e293b_0_50%,#334155_0_75%,#1e293b_0)] bg-[length:16px_16px]'
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          <div className='pt-2 flex items-center justify-between gap-4'>
            <p className='text-xs text-gray-500'>
              {anyReplacement
                ? 'Replaced assets upload immediately. Save Changes writes the name and finalizes the catalog record.'
                : 'No assets replaced — Save Changes will only update the name if you changed it.'}
            </p>
            <button
              onClick={handleSave}
              disabled={!canSave}
              className='shrink-0 px-6 py-2.5 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-60'
            >
              {saving ? 'Saving...' : anyUploadPending ? 'Uploading...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditLiveFrame;
